import { useCallback, useState } from 'react';

import { api, type ContractorRow, type Summary } from './api';
import { BarList, BarListSummary } from './components/Charts';
import {
  Card,
  CardHeader,
  Cell,
  ExportButton,
  Kpi,
  Row,
  SearchBar,
  Spinner,
  Table,
} from './components/Ui';
import { UnifiedTimeFilter } from './components/UnifiedTimeFilter';
import { useTimeFilter } from './context/TimeFilterContext';
import { useAsyncData } from './hooks/useAsyncData';
import { useSortedRows } from './hooks/useSortedRows';
import { MiniBarChart } from './components/KpiCard';
import { hours, money, moneyCompact, rate } from './format';
import { Shell, type ShellProps } from './Shell';

interface Data {
  readonly summary: Summary;
  readonly contractors: readonly ContractorRow[];
}

export function Contractors(props: ShellProps) {
  const { navigate } = props;
  const [search, setSearch] = useState('');
  const tf = useTimeFilter();

  const { data, error } = useAsyncData<Data>(
    () => {
      const dateRange = tf.toDateRange();
      return Promise.all([api.summary(dateRange), api.contractors(dateRange)]).then(
        ([summary, contractors]) => ({ summary, contractors }),
      );
    },
    [tf.selection],
    'Could not load contractor data.',
  );

  const accessor = useCallback((row: ContractorRow, key: string): unknown => {
    const map: Record<string, unknown> = {
      name: row.name,
      clientName: row.clientName,
      workerType: row.workerType,
      hours: row.hours,
      grossProfit: row.grossProfit,
      payRate: row.payRate,
      billRate: row.billRate,
      grossProfitPerHour: row.grossProfitPerHour,
    };
    return map[key];
  }, []);

  const filtered =
    data !== null
      ? data.contractors.filter(
          (c) =>
            search === '' ||
            c.name.toLowerCase().includes(search.toLowerCase()) ||
            c.clientName.toLowerCase().includes(search.toLowerCase()),
        )
      : [];

  const { sorted, sortKey, sortDirection, onSort } = useSortedRows(filtered, accessor);

  if (error !== null) {
    return (
      <Shell {...props}>
        <Card className="px-5 py-8">
          <p className="text-sm text-caution">{error}</p>
        </Card>
      </Shell>
    );
  }

  if (data === null) {
    return (
      <Shell {...props}>
        <Card>
          <Spinner label="Loading contractor data…" />
        </Card>
      </Shell>
    );
  }

  const { contractors } = data;
  const totalGp = contractors.reduce((s, c) => s + Number(c.grossProfit), 0).toFixed(4);
  const totalHours = contractors.reduce((s, c) => s + Number(c.hours), 0).toFixed(4);
  const w2Count = contractors.filter((c) => c.workerType === 'w2_employee').length;
  const c1099Count = contractors.filter((c) => c.workerType === 'contractor_1099').length;

  return (
    <Shell {...props}>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-ink">Contractors</h1>
          <p className="mt-1 text-sm text-ink-faint">
            Active contractors, rates, and profitability
          </p>
        </div>
        <UnifiedTimeFilter />
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <Kpi
          label="Total contractors"
          value={String(contractors.length)}
          hint={`${w2Count} W-2 · ${c1099Count} 1099`}
          delay={0}
        />
        <Kpi
          label="Total GP"
          value={moneyCompact(totalGp)}
          exact={money(totalGp)}
          tone="profit"
          delay={50}
        />
        <Kpi label="Total hours" value={hours(totalHours)} delay={100} />
        <Kpi
          label="Avg GP/hour"
          value={
            Number(totalHours) > 0 ? money((Number(totalGp) / Number(totalHours)).toFixed(4)) : '—'
          }
          tone="profit"
          delay={125}
        />
        <Kpi
          label="Avg GP/contractor"
          value={
            contractors.length > 0
              ? moneyCompact((Number(totalGp) / contractors.length).toFixed(4))
              : '—'
          }
          tone="profit"
          delay={150}
        />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <Card delay={200}>
          <CardHeader title="GP by contractor" subtitle="Click to view detail" />
          <BarListSummary
            viz={
              <MiniBarChart
                bars={contractors.slice(0, 15).map((c) => ({
                  label: c.name,
                  value: Number(c.grossProfit),
                }))}
              />
            }
          >
            <span>
              <span className="figure font-semibold">{moneyCompact(totalGp)}</span> GP
            </span>
            <span>
              <span className="figure font-semibold">{hours(totalHours)}</span> hours
            </span>
            <span>
              {contractors.length} contractor{contractors.length !== 1 ? 's' : ''}
            </span>
          </BarListSummary>
          <BarList
            data={contractors.slice(0, 20).map((c) => ({
              label: c.name,
              value: c.grossProfit,
              note: `${c.clientName} · ${hours(c.hours)} h`,
            }))}
            onClick={(index) => {
              const contractor = contractors[index];
              if (contractor !== undefined) navigate(`contractor/${contractor.id}`);
            }}
          />
        </Card>

        <Card className="lg:col-span-2" delay={250}>
          <CardHeader
            title="All contractors"
            subtitle="Click a row to see weekly detail"
            aside={
              <div className="flex items-center gap-3">
                <div className="w-48">
                  <SearchBar
                    value={search}
                    onChange={setSearch}
                    placeholder="Filter contractors…"
                  />
                </div>
                <ExportButton
                  data={sorted.map((c) => ({
                    name: c.name,
                    type: c.workerType === 'contractor_1099' ? '1099' : 'W-2',
                    client: c.clientName,
                    hours: hours(c.hours),
                    payRate: c.payRate ? rate(c.payRate) : '—',
                    billRate: c.billRate ? rate(c.billRate) : '—',
                    grossProfit: money(c.grossProfit),
                    gpPerHour: c.grossProfitPerHour ? money(c.grossProfitPerHour) : '—',
                  }))}
                  filename="contractors"
                  headers={[
                    { key: 'name', label: 'Contractor' },
                    { key: 'type', label: 'Type' },
                    { key: 'client', label: 'Client' },
                    { key: 'hours', label: 'Hours' },
                    { key: 'payRate', label: 'Pay Rate' },
                    { key: 'billRate', label: 'Bill Rate' },
                    { key: 'grossProfit', label: 'Gross Profit' },
                    { key: 'gpPerHour', label: 'GP/Hour' },
                  ]}
                />
              </div>
            }
          />
          <Table
            head={[
              { label: 'Contractor', sortKey: 'name' },
              { label: 'Type', sortKey: 'workerType' },
              { label: 'Client', sortKey: 'clientName' },
              { label: 'Hours', align: 'right', sortKey: 'hours' },
              { label: 'Pay rate', align: 'right', sortKey: 'payRate' },
              { label: 'Bill rate', align: 'right', sortKey: 'billRate' },
              { label: 'Gross profit', align: 'right', sortKey: 'grossProfit' },
              { label: 'GP/hour', align: 'right', sortKey: 'grossProfitPerHour' },
            ]}
            sortKey={sortKey}
            sortDirection={sortDirection}
            onSort={onSort}
          >
            {sorted.map((row) => (
              <Row key={row.id} onClick={() => navigate(`contractor/${row.id}`)}>
                <Cell>
                  <span className="cursor-pointer text-ink transition-colors hover:text-profit">
                    {row.name}
                  </span>
                </Cell>
                <Cell>
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                      row.workerType === 'contractor_1099'
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-blue-100 text-blue-800'
                    }`}
                  >
                    {row.workerType === 'contractor_1099' ? '1099' : 'W-2'}
                  </span>
                </Cell>
                <Cell>{row.clientName}</Cell>
                <Cell align="right" mono>
                  {hours(row.hours)}
                </Cell>
                <Cell align="right" mono>
                  {row.payRate ? rate(row.payRate) : '—'}
                </Cell>
                <Cell align="right" mono>
                  {row.billRate ? rate(row.billRate) : '—'}
                </Cell>
                <Cell align="right" mono>
                  <span className="text-profit">{money(row.grossProfit)}</span>
                </Cell>
                <Cell align="right" mono>
                  {row.grossProfitPerHour ? money(row.grossProfitPerHour) : '—'}
                </Cell>
              </Row>
            ))}
          </Table>
        </Card>
      </div>
    </Shell>
  );
}
